import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {createSelector} from "reselect";
import * as UTILS from "../../../utilities/utilities";

import {VARIABLES, COSTS} from "../../../utilities/constants";
import {TEXT} from "../../../utilities/strings";

import './HoverOver.css';

/**
 * Component for displaying a hover bubble above an svg chart.
 *
 * Should be used within a larger chart component, passing the
 * results of a mouse over from a chart element to render.
 *
 * @property data - an object with props, data, and cords fields
 *
 * @example
 * const hoverModel = {
 *    props: this.props,
 *    data: {fips: 123, value: 99},
 *    coords: [100, 200],
 * };
 * return (
 *   <HoverOver data={hoverModel} />
 * )
 */
export default class HoverOver extends PureComponent {

  static propTypes = {
    data: PropTypes.shape({
      props: PropTypes.object,
      data: PropTypes.object,
      coords: PropTypes.array
    })
  }

  wrapper = React.createRef()

  state = {
    x: 0,
    y: 0,
    content: '',
    visible: false,
    direction: 'down'
  };

  getData = (props) => props.data

  getHover = createSelector(
    [this.getData],
    (hoverData) => {
      const x = _.get(hoverData, 'coords[0]', 0);
      const y = _.get(hoverData, 'coords[1]', 0);
      const props = _.get(hoverData, 'props', {});
      const data = _.get(hoverData, 'data', {});

      const direction = this.getDirection(x, y);
      const content = this.createHoverContent(props, data);
      const visible = content && (x > 0 || y > 0);

      return {x, y, content, direction, visible};
    }
  )

  getPosition = createSelector(
    [this.getHover],
    (hover) => ({
      width: 0,
      height: 0,
      left: hover.x,
      top: hover.y,
      overflow: 'visible !important',
      position: 'relative',
      display: hover.visible ? 'block' : 'none'
    })
  )

  /**
   * Get best hover direction based on x,y coordinates
   *
   * @param x
   * @param y
   * @returns {*}
   */
  getDirection = (x, y) => {

    if (!this.wrapper.current) {
      return 'down'
    }

    const bounds = this.wrapper.current.getBoundingClientRect();
    const globalWidth = window.innerWidth;
    const globalX = x + bounds.left;
    const globalY = y + bounds.top;
    const xMargin = 150;
    const yMargin = 200;

    if (globalX < xMargin) {
      return 'left';
    } else if (globalX > globalWidth - xMargin) {
      return 'right';
    } else if (globalY < yMargin) {
      return 'up';
    } else {
      return 'down';
    }
  }

  /**
   * Using props and data based on the selectedCost
   * create textual content based off the county/state
   * and cost columns
   *
   * @param props
   * @param data
   * @returns {*}
   */
  createHoverContent = (props, data) => {
    if (!data) {
      return null;
    }

    // Create best name from project specific data sources
    let name = _.get(data, VARIABLES.Location) || TEXT.location;
    let rawColumns = UTILS.getDetailColumns(props.selectedCost || COSTS.TOTAL)
    let columns = _.filter(rawColumns, c => c.field !== VARIABLES.Location)

    let getFormattedValue = (item, column) => {
      let value = _.get(item, column.field);

      if (!value) {
        return TEXT.suppressed_data_abbr;
      }

      if (value && column.format) {
        value = column.format(value);
      }

      return value
    }

    let values = _.map(columns, col => (
      <div key={col.field}>
        <b>{col.label}</b>:&nbsp;&nbsp;
        <span className="text-muted">{getFormattedValue(data, col)}</span>
      </div>
    ))

    return (
      <div>
        <h4>{name}</h4>
        {values}
      </div>
    );
  }

  render() {
    const positionStyle = this.getPosition(this.props);
    const {direction, content, visible} = this.getHover(this.props);

    return visible && (
      <div ref={this.wrapper} className="HoverOver wrapper screen-reader-hidden">
        <div className="position" style={positionStyle}>
          <div className={"d3tooltip arrow-tooltip-" + direction}>
            {content}
          </div>
        </div>
      </div>
    );
  }
}
